<template>
  <component
    :is="fieldComponent"
    v-bind="boundFieldProps"
    :options="fieldProps.options ?? []"
    v-model="fieldVal"
    :field-name="fieldName"
    :validation-iterator="validationIterator"
    :external-error="fieldProps.error"
    :should-validate="shouldValidate"
    @clear-external-error="fieldName => $emit('clear-external-error', fieldName)"
    @update:error="emitHasErrors" />
</template>

<script>
// Any component possibilities for the :is prop need to be imported here and registered within this component
import {
  DropDownField,
  TextField,
  PasswordField,
  TextArea,
  SingleDatePicker,
  VSwitch,
  YesNoToggle,
  PhoneNumberField,
  TimeStampField,
  MultiDropDownField,
  ComboBox,
  DocumentUpload
} from '@/components';

import MultiRefNumberManager from '@satellite/components/elements/MultiRefNumberManager';
import EmailListField from '@/components/elements/form/EmailListField.vue';
import { camelCase, omit, upperFirst } from 'lodash';

/**
 * Component used to render provided fields in the Basic Form component.
 * Can also be used ad-hoc as needed
 * @displayName Basic Form Field
 */
export default {
  name: 'BasicFormField',
  components: {
    DropDownField,
    TextField,
    PasswordField,
    EmailListField,
    TextArea,
    SingleDatePicker,
    VSwitch,
    YesNoToggle,
    PhoneNumberField,
    TimeStampField,
    MultiDropDownField,
    ComboBox,
    DocumentUpload,
    MultiRefNumberManager
  },
  props: {
    /**
     * Iterator to programmatically trigger validation on the field
     */
    validationIterator: {
      type: Number,
      required: false
    },
    /**
     * Used for id/name attributes for the field
     */
    fieldName: {
      type: String,
      required: true
    },
    /**
     * Passthrough props to dynamic component
     */
    fieldProps: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    /**
     * Dynamic component to render
     */
    fieldComponent: {
      type: String,
      required: true
    },
    shouldValidate: {
      type: Boolean,
      required: false
    }
  },
  data() {
    return {
      fieldVal: null
    };
  },
  computed: {
    boundFieldProps() {
      // `value` already flows through v-model. Components without a `value` prop would get it as a
      // fallthrough attribute, which EmailListField puts on its <input> and reads back as typing.
      const component = this.$options.components[upperFirst(camelCase(this.fieldComponent))];
      return component?.props?.value ? this.fieldProps : omit(this.fieldProps, 'value');
    }
  },
  methods: {
    emitHasErrors(hasErrors) {
      /**
       * @event update:error
       * @property {boolean} hasErrors
       */
      this.$emit('update:error', hasErrors);
    }
  },
  created() {
    this.fieldVal = this.fieldProps.value;
  },
  watch: {
    'fieldProps.value'(newVal) {
      if (newVal !== this.fieldVal) {
        this.fieldVal = newVal;
      }
    },
    fieldVal(newVal) {
      this.$emit('update:value', { ...this.fieldProps, value: newVal });
    }
  }
};
</script>
